/** The mission queue: what waits and why, and what the scheduler started or could not start. */
import { ArrowRightLeft, ListOrdered, Pin, RotateCcw, X } from '@orgus/ui/icons';
import { useState } from 'react';
import { Link } from 'react-router';
import { AUTO_AGENT, ITEM_PRIORITY_LABEL, agentKindLabel, failoverLine } from '@orgus/protocol';
import type { Id, ItemPriority, Machine, Mission, Project, QueueEntry } from '@orgus/protocol';
import { Badge, Button, EmptyState, GROUP_INDENT, GroupChevron, IconButton, Pressable, REVEAL_GROUP, RelativeTime, RevealSlot, RowStatus, cn, formatInt, toast } from '@orgus/ui';
import type { RowStatusProps } from '@orgus/ui';
import { cancelEntry, retryEntry } from './data.ts';
import { waitLabel, waitTone } from './model.ts';
import { HoldNote, useHold } from '../project-health/Holds.tsx';
import { FactGrid } from '../detail/FactGrid.tsx';
import { AgentKindBadge, AgentKindLabel } from '../adapters/AgentKind.tsx';

/** The kind an entry runs (or ran) with: placement's pick, else the kind it is pinned to; null for 'auto' not placed yet. */
function agentOf(e: Pick<QueueEntry, 'agentKind' | 'needs'>): string | null {
  return e.agentKind ?? (e.needs.runtime !== AUTO_AGENT ? e.needs.runtime : null);
}

/**
 * The row's one chip: the agent. After a failover (ADR 0049 decision 5) it says which agents it
 * moved from, "Claude Code after Codex".
 */
function AgentChip({ e }: { e: QueueEntry }) {
  const kind = agentOf(e);
  if (!kind) return null;
  const from = e.switches ? [...new Set((e.failovers ?? []).slice(0, e.switches).map((f) => agentKindLabel(f.kind)))] : [];
  if (!from.length) return <AgentKindBadge kind={kind} />;
  return (
    <Badge tone="neutral" variant="outline" icon={<ArrowRightLeft />} className="shrink-0" title={`Moved to ${agentKindLabel(kind)} after ${from.join(' and ')} failed`}>
      {agentKindLabel(kind)} after {from.join(', ')}
    </Badge>
  );
}

/** A queue entry's one status, plain as in every row: a reason a person can fix is amber. */
export function queueRowStatus(e: Pick<QueueEntry, 'state' | 'reason'>): Pick<RowStatusProps, 'tone' | 'icon' | 'label'> {
  const label = waitLabel(e);
  switch (e.state) {
    case 'queued': {
      const t = waitTone(e.reason);
      return t === 'waiting' ? { tone: 'attention', icon: 'warning', label } : t === 'offline' ? { tone: 'neutral', icon: 'offline', label } : { tone: 'neutral', icon: 'queued', label };
    }
    case 'starting':
      return { tone: 'working', icon: 'working', label };
    case 'started':
      return { tone: 'neutral', icon: 'done', label };
    case 'failed':
      return { tone: 'danger', icon: 'failed', label };
    default:
      return { tone: 'neutral', icon: 'stopped', label };
  }
}

/**
 * One entry: status, title, why or where, and when, on two lines. The rest (the scheduler's full
 * reason, priority, owner, what it needs) opens under it on click. A landing or deploy it yields
 * to shows at rest, with Start now.
 */
function QueueRow({
  e,
  mission: m,
  machines,
  projects,
  onAct,
}: {
  e: QueueEntry;
  mission: Mission | undefined;
  machines: Record<Id, Machine>;
  projects: Record<Id, Project>;
  onAct: (fn: () => Promise<unknown>, done: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const hold = useHold('mission', { id: e.id, label: e.title, projectId: e.projectId });
  const placed = e.placedOn ? (machines[e.placedOn]?.name ?? 'a removed machine') : null;
  const pinned = e.machineId ? (machines[e.machineId]?.name ?? 'a removed machine') : null;
  const st = queueRowStatus(e);
  const when = e.state === 'started' ? (e.startedAt ?? e.updatedAt) : e.state === 'queued' ? e.enqueuedAt : e.updatedAt;
  const project = projects[e.projectId]?.name ?? 'Unknown project';
  const canCancel = e.state === 'queued';
  const canRetry = (e.state === 'failed' || e.state === 'cancelled') && !!m && !m.closed;
  return (
    <li className={cn(REVEAL_GROUP, 'relative flex flex-col')}>
      <Pressable
        data-row=""
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className="flex min-h-row-2line min-w-0 items-center gap-2 rounded-control px-3 py-2 hover:bg-hover sm:h-row-2line sm:py-0"
      >
        <GroupChevron open={open} />
        <RowStatus {...st} hideLabel focusable={false} />
        <span className="flex min-w-0 flex-1 flex-col">
          {/* On a phone the title takes the row's width (the time moves to the meta line) and wraps
              to a second line rather than shrinking to a letter. */}
          <span className="line-clamp-2 text-ui font-medium text-fg sm:truncate">{e.title}</span>
          {/* On a phone the facts wrap as whole facts rather than cutting the project to a letter. */}
          <span className="flex min-w-0 flex-wrap gap-x-2 text-caption text-fg-subtle sm:flex-nowrap">
            <span className={cn('shrink-0', st.tone === 'attention' && 'text-status-attention', st.tone === 'danger' && 'text-status-danger')}>
              {st.label}
            </span>
            <span data-meta="" className="shrink-0 whitespace-nowrap num sm:hidden">
              <RelativeTime ts={when} />
            </span>
            <span data-meta="" className="max-w-full min-w-0 truncate">
              {placed && e.state !== 'queued' ? `${project} on ${placed}` : project}
            </span>
          </span>
        </span>
        <span className="hidden shrink-0 sm:inline-flex">
          <AgentChip e={e} />
        </span>
        <span className="hidden w-16 shrink-0 text-right text-caption whitespace-nowrap text-fg-subtle num sm:inline">
          <RelativeTime ts={when} />
        </span>
        {/* Room for the hover actions, so they never cover the time: the same width on every row
            from sm up (the times line up), only what this row's action needs on a phone. */}
        <span className={cn('shrink-0 sm:w-24', canRetry ? 'w-24' : canCancel ? 'w-8' : 'hidden sm:block')} aria-hidden />
      </Pressable>
      <RevealSlot className="absolute top-3 right-3 flex items-center gap-1">
        {canCancel && <IconButton label={`Cancel ${e.title}`} icon={<X />} size="sm" onClick={() => onAct(() => cancelEntry(e.id), 'Removed from the queue')} />}
        {canRetry && (
          <Button size="sm" variant="ghost" icon={<RotateCcw />} onClick={() => onAct(() => retryEntry(e.id), 'Queued again')}>
            Queue again
          </Button>
        )}
      </RevealSlot>
      {hold && !open && <HoldNote hold={hold} className={cn('px-3 pb-2', GROUP_INDENT, 'ml-6')} />}
      {open && (
        <div className={cn('flex flex-col gap-4 px-3 pt-1 pb-4', GROUP_INDENT, 'ml-6')}>
          {hold ? <HoldNote hold={hold} /> : e.message && <p className={cn('text-ui', e.state === 'failed' ? 'text-status-danger' : 'text-fg-muted')}>{e.message}</p>}
          {e.state !== 'failed' && !!e.failovers?.length && (
            <ul className="flex flex-col gap-1 text-caption text-fg-muted" aria-label="What it moved past">
              {e.failovers.map((f) => (
                <li key={`${f.kind}-${f.at}`} className="flex min-w-0 items-center gap-2">
                  <ArrowRightLeft className="size-3 shrink-0 text-fg-subtle" aria-hidden />
                  {/* ADR 0049 decision 5: another agent; ADR 0071 decision 9: a per-token account of the same agent. */}
                  <span className="min-w-0">{failoverLine(f)}</span>
                </li>
              ))}
            </ul>
          )}
          {/* Why placement picked this agent for an 'auto' mission (ADR 0049 decision 5): one quiet line. */}
          {e.agentReason && agentOf(e) && (
            <p className="text-caption text-fg-subtle" data-agent-reason="">
              Why {agentKindLabel(agentOf(e))}: {e.agentReason}
            </p>
          )}
          <FactGrid
            lines={[
              [
                { label: 'Priority', value: ITEM_PRIORITY_LABEL[Math.max(0, Math.min(3, e.priority)) as ItemPriority] },
                { label: 'For', value: e.person },
                { label: 'Needs', value: e.needs.labels.length ? e.needs.labels.join(', ') : 'Any machine', mono: e.needs.labels.length > 0 },
                { label: 'Free memory', value: e.needs.minFreeMemGb != null ? `${formatInt(e.needs.minFreeMemGb)} GB` : 'Any' },
              ],
              [
                {
                  label: 'Machine',
                  value: pinned ? (
                    <>
                      <Pin className="size-3 shrink-0" aria-hidden />
                      <span className="truncate">Only {pinned}</span>
                    </>
                  ) : (
                    (placed ?? 'First that fits')
                  ),
                },
                { label: 'Agent', value: agentOf(e) ? <AgentKindLabel kind={agentOf(e)} /> : 'Any working agent' },
                { label: 'Account', value: e.account?.name ?? null },
                {
                  label: 'Mission',
                  value: m ? (
                    <Link to={`/missions/${encodeURIComponent(m.id)}`} className="truncate text-accent-text hover:underline">
                      Open mission
                    </Link>
                  ) : null,
                },
              ],
              [{ label: 'Attempts', value: e.attempts > 1 ? formatInt(e.attempts) : null }],
            ]}
          />
        </div>
      )}
    </li>
  );
}

export function QueueList({
  entries,
  missions,
  machines,
  projects,
  history,
  onQueue,
}: {
  entries: QueueEntry[];
  missions: Record<Id, Mission>;
  machines: Record<Id, Machine>;
  projects: Record<Id, Project>;
  history: boolean;
  onQueue: () => void;
}) {
  if (!entries.length) {
    return history ? (
      <EmptyState icon={<ListOrdered />} title="Nothing has run from the queue yet" description="Missions the scheduler started or could not start show here" />
    ) : (
      <EmptyState
        icon={<ListOrdered />}
        title="Nothing is waiting"
        description="A queued mission starts on the first machine that fits"
        action={
          <Button variant="primary" onClick={onQueue}>
            Queue mission
          </Button>
        }
      />
    );
  }
  const act = async (fn: () => Promise<unknown>, done: string) => {
    try {
      await fn();
      toast.success(done);
    } catch (e) {
      toast.error(e);
    }
  };
  return (
    <ul className="flex flex-col p-1" aria-label={history ? 'Queue history' : 'Waiting missions'}>
      {entries.map((e) => (
        <QueueRow key={e.id} e={e} mission={missions[e.missionId]} machines={machines} projects={projects} onAct={(fn, done) => void act(fn, done)} />
      ))}
    </ul>
  );
}
